/**
 * inflight — tiny subscribable counter of in-flight XHR requests.
 *
 * Drives the `<NetworkIndicator>` in the TopBar and anything else
 * that needs to visualise network activity. Incremented by the
 * apiClient request interceptor, decremented by the response
 * interceptor (both success + failure paths).
 *
 * Uses React 19's `useSyncExternalStore` contract so the hook is
 * tear-safe and doesn't cause extra renders when the count holds
 * steady between two consecutive requests.
 */

import { useSyncExternalStore } from 'react';

let count = 0;
const listeners = new Set<() => void>();

function notify() {
    for (const l of listeners) {
        try { l(); } catch (err) { console.error('[inflight] listener threw', err); }
    }
}

export function beginRequest(): void {
    count += 1;
    notify();
}

export function endRequest(): void {
    // Clamp at 0 so an unmatched decrement (e.g. interceptor error)
    // can't wrap the counter into negative territory.
    count = Math.max(0, count - 1);
    notify();
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

/** True when any request is in flight. Stable equality — subscribers
 *  only re-render on the busy/idle transition. */
export function useIsNetworkBusy(): boolean {
    return useSyncExternalStore(
        subscribe,
        () => count > 0,
        () => count > 0,
    );
}
